import * as Sentry from '@sentry/browser';
import {
    defaultDataIdFromObject,
    InMemoryCache,
    InMemoryCacheConfig,
    IntrospectionFragmentMatcher,
    NormalizedCacheObject,
} from 'apollo-cache-inmemory';
import { ApolloClient, ApolloClientOptions } from 'apollo-client';
import { ApolloLink, DocumentNode } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';
import { createHttpLink } from 'apollo-link-http';
import {
    CoachNode,
    MeditationNode,
    ProgramNode,
    ScheduledContentLinkNode,
    ScheduledContentNode,
} from 'src/generated/webapp_gql';
import providerResolvers from 'src/modules/providers/resolvers';
import programResolvers from 'src/modules/newPrograms/resolvers';
import meditationResolvers from 'src/modules/meditations/resolvers';
import introspectionQueryResultData from 'src/generated/fragmentTypes.json';

import { authenticationService } from './rootService';

import ApolloTypeDefs from './apollo.graphql';
import { captureException } from 'src/shared/helpers/captureException';

const authLink = setContext(async (_, { options }) => {
    const headers = { ...options };
    const authData = await authenticationService.getValidAuthDataOrRefresh();

    if (authData) {
        headers.Authorization = `${authData.tokenType} ${authData.accessToken}`;
    }

    return { headers };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        if (Array.isArray(graphQLErrors)) {
            graphQLErrors.forEach(error => {
                Sentry.captureException(error);
                captureException(error);
            });
        } else {
            Sentry.captureException(graphQLErrors);
            captureException(graphQLErrors);
        }

        graphQLErrors.map(({ message, locations, path }) =>
            // eslint-disable-next-line no-console
            console.info(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
            ),
        );
    }

    if (networkError) {
        Sentry.captureException(networkError);
        captureException(networkError);
        // eslint-disable-next-line no-console
        console.info(`[Network error]: ${networkError}`);
    }
});

const sentryBreadCrumbLink = new ApolloLink((operation, forward) => {
    // add the request to sentry breadcrumb
    Sentry.addBreadcrumb({
        category: 'graphql',
        message: JSON.stringify(operation),
    });

    return forward(operation);
});

/**
 * Used to represent a type with a unique ID under a 'uuid' key.
 *
 * Constructed from the union of the nodes with a UUID in our
 * dataIdFromObject hook.
 */
type UuidGetterNode =
    | MeditationNode
    | ProgramNode
    | ScheduledContentNode
    | ScheduledContentLinkNode
    | CoachNode;

export interface AppClientOptions extends ApolloClientOptions<NormalizedCacheObject> {
    typeDefs: DocumentNode;
}

export const buildAppClientOptions = (
    cacheOptions?: InMemoryCacheConfig,
    options?: Partial<AppClientOptions>,
): AppClientOptions => {
    const fragmentMatcher = new IntrospectionFragmentMatcher({
        introspectionQueryResultData,
    });

    const cache = new InMemoryCache({
        fragmentMatcher,
        dataIdFromObject: object => {
            switch (object.__typename) {
                case 'PageNode':
                    return `${object.id}`;
                case 'ProgramNode':
                case 'MeditationNode':
                case 'ScheduledContentLinkNode':
                case 'ScheduledContentNode':
                case 'CoachNode':
                    return (object as UuidGetterNode).uuid;
                default:
                    return defaultDataIdFromObject(object);
            }
        },
        ...cacheOptions,
    });

    // Seed the cache with the default programs value...
    cache.writeData({
        data: {
            programs: null,
            program: null,
            programBySlug: null,
        },
    });

    return {
        cache,
        typeDefs: ApolloTypeDefs,
        resolvers: {
            ...providerResolvers,
            ...programResolvers,
            ...meditationResolvers,
        },
        ...options,
    };
};

export type AppClient = ApolloClient<NormalizedCacheObject>;

export const createAppClient = (): AppClient => {
    const httpLink = createHttpLink({
        uri: process.env.REACT_APP_GRAPHQL_URL,
    });

    const link = ApolloLink.from([sentryBreadCrumbLink, errorLink, authLink, httpLink]);

    return new ApolloClient({
        ...buildAppClientOptions({}, { link }),
    });
};

export let appClient: AppClient;

if (process.env.NODE_ENV === 'test') {
    // We can't import createMswTestClient here,
    // without importing all test utilities...
    const link = createHttpLink({
        uri: 'http://test.local/graphql',
    });

    const client = new ApolloClient({
        ...buildAppClientOptions({}, { link }),
    });

    appClient = client;
} else {
    appClient = createAppClient();
}
